import type { BusinessState } from "./types";

/**
 * Words for what core sends as codes. Kept with the client so a screen never
 * carries a name of its own.
 */

/** Review item keys core uses (ReviewItem.itemKey / kind). */
const ITEM_LABELS: Record<string, string> = {
  pan: "Company PAN",
  gst_certificate: "GST certificate",
  gstin: "GSTIN",
  cin_certificate: "Certificate of Incorporation (CoI) / CIN",
  company_record: "Company record",
  directors: "Directors",
  moa: "Memorandum of Association",
  aoa: "Articles of Association",
  principal_place: "Principal place of business",
  bank_account: "Bank account",
  registration: "Registration",
};

const KIND_LABELS: Record<string, string> = {
  company_document: "Company document",
  company_details: "Company details",
  office_proof: "Office proof",
  team_letter: "Team letter",
  identity: "Identity",
  signatory: "Signatory",
  bank_account: "Bank account",
};

function humanise(code: string): string {
  const words = code.replace(/[_:]/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function itemLabel(itemKey: string | null, kind: string): string {
  if (itemKey && ITEM_LABELS[itemKey]) return ITEM_LABELS[itemKey];
  if (itemKey && itemKey.startsWith("board_resolution")) {
    return "Board resolution";
  }
  return KIND_LABELS[kind] ?? humanise(itemKey ?? kind);
}

/** The status words of the Applications list and the review header. */
export type ApplicationStatus =
  | "not_submitted"
  | "invite_sent"
  | "in_progress"
  | "waiting"
  | "sent_back"
  | "approved"
  | "not_approved"
  | "closed";

export const APPLICATION_STATUS_LABELS: Record<ApplicationStatus, string> = {
  not_submitted: "Not submitted",
  invite_sent: "Invite sent",
  in_progress: "In progress",
  waiting: "Waiting for review",
  sent_back: "Sent back",
  approved: "Approved",
  not_approved: "Not approved",
  closed: "Closed",
};

/**
 * The list answers only `state`; it cannot say whether the invite was opened,
 * so a not_submitted row reads "Not submitted" there. The detail knows the
 * latest invite: sent is "Invite sent"; opened or used is "In progress".
 */
export function applicationStatus(
  state: BusinessState | null,
  inviteStatus?: string | null,
): ApplicationStatus {
  switch (state) {
    case "submitted":
    case "in_review":
      return "waiting";
    case "fix_asked":
      return "sent_back";
    case "registered":
      return "approved";
    case "not_approved":
      return "not_approved";
    case "closed":
      return "closed";
    default:
      if (inviteStatus === undefined) return "not_submitted";
      return inviteStatus === "sent" ? "invite_sent" : "in_progress";
  }
}

/** Resend works only while the latest invite is open; core answers 409 no_open_invite otherwise. */
export const inviteIsOpen = (inviteStatus: string | null | undefined) =>
  inviteStatus === "sent" || inviteStatus === "opened";

export const APPLICATION_STATUS_FILTER: ApplicationStatus[] = [
  "not_submitted",
  "waiting",
  "sent_back",
  "approved",
  "not_approved",
];

export type DueTone = "overdue" | "today" | "within";

/** Where a due date stands today. */
export function dueTone(dueAt: string | null, now = new Date()): DueTone | null {
  if (!dueAt) return null;
  const due = new Date(dueAt);
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const endOfToday = new Date(startOfToday);
  endOfToday.setDate(endOfToday.getDate() + 1);
  if (due < startOfToday) return "overdue";
  if (due < endOfToday) return "today";
  return "within";
}

export const DUE_LABELS: Record<DueTone, string> = {
  overdue: "Overdue",
  today: "Due today",
  within: "Within SLA",
};

/** "3h ago", "yesterday", "2 days ago". */
export function timeAgo(iso: string | null, now = new Date()): string {
  if (!iso) return "";
  const diffMs = now.getTime() - new Date(iso).getTime();
  const hours = Math.floor(diffMs / 3_600_000);
  if (hours < 1) return "just now";
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "yesterday";
  return `${days} days ago`;
}

/** "submitted 3h ago" once submitted, "invited 2 days ago" before. */
export function registrationLine(b: { submittedAt: string | null; createdAt: string }): string {
  return b.submittedAt
    ? `Registration · submitted ${timeAgo(b.submittedAt)}`
    : `Registration · invited ${timeAgo(b.createdAt)}`;
}
